<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { ReportDesign, Table } from "../../../types/report";
import Buttons from "../../ui/form/Buttons.vue";
import SearchSelect from "../../ui/form/SearchSelect.vue";
import AddTable from "./AddTable.vue";
import IconComponent from "../../ui/common/IconComponent.vue";
import MessageBox from "../../../services/MessageBox";
import { moduleToTitle } from "../../../utils/CommonFunctions";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import { mapSqlTypeToVueType } from "../../../types/mapSqlTypeToVueType";
import CheckInput from "../../ui/form/CheckInput.vue";
import TextAreaInput from "../../ui/form/TextAreaInput.vue";

const props = defineProps<{
  report: ReportDesign;
  data?: any;
  mode?: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: "queryChanged", data: string): void;
}>();

const showTableScreen = ref(false);
const selectedTable = ref<Table>();

const handleBaseTableSelect = async (table: any) => {
  if (table) {
    props.report.baseTable = table.id;
    props.report.baseTableAlias = table.alias || table.id;
    const fields = await getFields(table);
    if (props.report.tables.length > 0) {
      const baseTableIndex = props.report.tables.findIndex(
        (t) => t.baseTable === true
      );
      if (baseTableIndex !== -1) {
        props.report.tables[baseTableIndex].name = table.name;
        props.report.tables[baseTableIndex].alias = table.alias || table.name;
      } else {
        props.report.tables.push({
          id: "table_" + Math.random().toString(36).substring(2, 15), // Generate a unique ID
          displayName: table.name,
          raw: false,
          rawQuery: "",
          name: table.id,
          alias: table.id,
          fields: fields,
          baseTable: true,
        });
      }
    } else {
      let t = {
        id: "table_" + Math.random().toString(36).substring(2, 15), // Generate a unique ID
        displayName: table.name,
        name: table.id,
        alias: table.id,
        raw: false,
        rawQuery: "",
        fields: fields,
        baseTable: true,
      };
      props.report.tables = [t];
    }
  }
};

const getFields = async (table: Table) => {
  try {
    const response = await api.post("/clients/reports/fields", {
      table: table.id,
    });
    if (response.success == true) {
      let data = response.data;
      data.forEach((field: { field_type: string }) => {
        field.field_type = mapSqlTypeToVueType(field.field_type);
      });
      return data;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Source Fields", "error");
    return [];
  }
};

const removeTable = (index: number) => {
  MessageBox.confirm({
    title: "Confirm Delete",
    message:
      "Are you sure you want to delete the table (" +
      props.report.tables[index].displayName +
      ")?",
    confirmText: "Yes, Delete",
    cancelText: "Cancel",
  }).then((confirmed: any) => {
    if (confirmed) {
      if (props.report.tables[index].baseTable) {
        props.report.baseTable = "";
        props.report.baseTableAlias = "";
      }
      props.report.tables.splice(index, 1);
    }
  });
};

const handleTableUpdated = (table: any) => {
  if (table) {
    const existingTableIndex = props.report.tables.findIndex(
      (t) => t.id === table.id
    );
    if (existingTableIndex !== -1) {
      props.report.tables[existingTableIndex] = table;
    } else {
      props.report.tables.push(table);
    }
  }
  showTableScreen.value = false;
};

const reportQuery = ref('')
const isReadOnly = ref(true);

// Load report if editing existing one
onMounted(() => {
  isReadOnly.value = props.mode == "view";
});

watch(() => reportQuery.value, () => {
  emit('queryChanged', reportQuery.value)
})
</script>

<template>
  <div class="data-source section">
    <div class="section-header" style="display: flex; justify-content: space-between; align-items: center;">
      <div id="head" style="display: flex; align-items: center;">
      <div class="step-indicator">Step 2</div>
      <h2 style="margin-left: 8px;">Tables</h2>
      </div>
      <CheckInput
      v-model="props.report.rawReport"
      label="Use Raw SQL Query"
      class="ml-4"
      />
    </div>
    <div class="form-group" v-if="props.report.baseTable == '' && !props.report.rawReport">
      <SearchSelect
        label="Main Table"
        v-model="props.report.baseTable"
        @select="handleBaseTableSelect"
        :readonly="props.report.baseTable != ''"
        custom-url="reports/tables"
        placeholder="Select Main Table"
      />
    </div>
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>

    <div class="table-section" v-if="props.report.tables.length > 0 && !props.report.rawReport">
      <div
        class="section-header"
        style="display: flex; justify-content: flex-end"
      >
        <Buttons
          caption="Add Table"
          icon="Plus"
          @click="showTableScreen = true"
        />
      </div>

      <div class="relative hidden md:block">
        <div v-if="props.report.tables.length === 0" class="empty-state">
          <p>No Tables added. Click "Add Table" to add tables.</p>
        </div>
        <table class="divide-y divide-gray-200 dark:divide-gray-700 w-full">
          <thead class="bg-gray-50 dark:bg-gray-800">
            <tr
              class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              <th class="text-left">Table Name</th>
              <th class="text-left">Alias</th>
              <th class="text-left">Type</th>
              <th class="text-left">Field Name</th>
              <th class="text-left">Joined Table Name</th>
              <th class="text-left">Join Type</th>
              <th class="text-left">Target Field</th>
              <th
                class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-800 shadow-l"
              >
                ...
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white text-sm py-4 dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <tr
              v-for="(table, index) in props.report.tables"
              :key="index"
              class="hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <td class="py-4">{{ table.displayName }}</td>
              <td class="py-4">{{ table.alias }}</td>
              <td class="py-4">
                {{ table.baseTable ? "Main Table" : "Joined Table" }}
              </td>
              <td class="py-4">{{ moduleToTitle(table.sourceField ?? "") }}</td>
              <td class="py-4">
                {{ moduleToTitle(table.targetTableName ?? "") }}
              </td>
              <td class="py-4">{{ table.joinType }}</td>
              <td class="py-4">{{ moduleToTitle(table.targetField ?? "") }}</td>
              <td class="whitespace-nowrap text-right sticky right-0 shadow-l">
                <button
                  id="table-action-remove-table"
                  @click="removeTable(index)"
                  class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600"
                  title="Delete Table"
                >
                  <IconComponent name="Trash2" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="table-section" v-if="props.report.rawReport">
      <TextAreaInput
            v-model="reportQuery"
            label="SQL Query"
            class="w-full col-span-2"
            :readonly="isReadOnly" />
    </div>
  </div>
  <AddTable
    v-if="showTableScreen"
    :mode="selectedTable ? 'edit' : 'create'"
    :title="
      selectedTable
        ? 'Edit' + selectedTable.name + '(' + selectedTable.alias + ')'
        : 'Add New Table'
    "
    :data="selectedTable"
    :report="report"
    @close="showTableScreen = false"
    @select-table="handleTableUpdated"
  />
</template>
